import PageSEO from "@/components/PageSEO";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTAFooter from "@/components/CTAFooter";
import SiteFooter from "@/components/SiteFooter";
import { Phone, ClipboardList, FileText, CalendarClock, HardHat, Zap, ClipboardCheck, Wrench, CheckCircle } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: Phone,
    title: "Enquiry",
    description:
      "It starts with a call to 0480 841 485 or a message through our website. We ask about your home — how many rooms need heating or cooling, what's not working with your current setup, roughly how old the house is, and which suburb you're in, since elevation and frost exposure vary a lot across the Southern Highlands. There's no pressure at this stage; it's simply enough information for us to understand whether you're likely looking at a single split system, a multi-split, or ducted air conditioning, and to book an on-site assessment at a time that suits you.",
  },
  {
    number: "02",
    icon: ClipboardList,
    title: "On-Site Assessment & Heat-Load Sizing",
    description:
      "A technician visits your property to properly assess the job rather than guess from floor area. We measure room dimensions and ceiling height, check insulation and window glazing, note orientation and shading, and assess how exposed the property is to frost, wind and cold-air pooling — all of which change the real heating and cooling load. For ducted jobs we also check roof space access and existing ductwork condition. This step is what allows us to recommend a system that won't struggle on a sub-zero morning or short-cycle and waste energy because it's oversized.",
  },
  {
    number: "03",
    icon: FileText,
    title: "Written Quote",
    description:
      "You receive a detailed, itemised written quote covering the equipment, installation scope, any electrical work required, and structural considerations such as bracket type, wall penetrations or ductwork runs. Pricing is fixed and transparent — there are no vague allowances or surprise inclusions added once work has started. If you're comparing options, such as a multi-split versus a small ducted zone, we'll set out the trade-offs in plain terms so the decision is genuinely yours.",
  },
  {
    number: "04",
    icon: CalendarClock,
    title: "Scheduling",
    description:
      "Once you're ready to proceed, we confirm the scope, take a deposit, and lock in an installation date. Timeframes vary with season — demand for heating installations understandably increases as autumn turns to winter, so booking earlier in the year generally means more flexibility on dates. We'll give you a realistic window rather than an optimistic one, and confirm details again shortly before the job.",
  },
  {
    number: "05",
    icon: HardHat,
    title: "Installation Day",
    description:
      "Our technicians arrive on time, briefed on the plan from the assessment and quote. Indoor and outdoor units are mounted, refrigerant piping and drainage are run, and we take care to protect flooring and finishes throughout. Outdoor units are positioned to avoid frost pockets and cold-air pooling where the site allows, with clearance for airflow and future servicing. We keep you informed of progress and clean up thoroughly before we finish for the day.",
  },
  {
    number: "06",
    icon: Zap,
    title: "Electrical Work & Commissioning",
    description:
      "New circuits, isolators and wiring are installed to Australian wiring standards. Once the mechanical and electrical work is complete, we pressure-test and evacuate the refrigerant circuit, charge the system correctly, and run it through both heating and cooling modes to confirm it reaches its rated output. This commissioning step is where a lot of the long-term reliability of a system is decided, and it's carried out by our ARC-licensed technicians as required by law for any refrigerant work.",
  },
  {
    number: "07",
    icon: ClipboardCheck,
    title: "Handover & Documentation",
    description:
      "Before we leave, we walk you through operating the system — heating and cooling modes, timers, filter access and cleaning, and what to expect from a defrost cycle on a frosty morning so it's never mistaken for a fault. You're provided with manufacturer warranty documentation, electrical compliance certificates, and our workmanship warranty terms in writing, plus an invoice and service record for your files.",
  },
  {
    number: "08",
    icon: Wrench,
    title: "Aftercare & Maintenance",
    description:
      "Our relationship doesn't end at handover. We recommend an annual service, ideally before winter, to check refrigerant pressures, clean the outdoor coil, test drainage and confirm electrical connections are sound before the coldest months place the heaviest demand on the system. We also offer a maintenance membership with scheduled servicing and priority booking — see our Membership page for details — and our team remains available for repairs on systems we've installed as well as most other major brands.",
  },
];

const checklistItems = [
  "Room dimensions, ceiling height and window sizing in every space to be conditioned",
  "Insulation quality in walls, ceiling and underfloor where accessible",
  "Orientation and shading, including exposure to afternoon sun or prevailing cold winds",
  "Site elevation and known frost patterns for the specific property",
  "Existing ductwork condition and roof space access, where relevant",
  "Available power supply and whether new circuits or an upgraded switchboard are needed",
  "Suitable outdoor unit locations, avoiding low-lying frost pockets and gutter run-off",
  "Pipe and cable run distances between proposed indoor and outdoor unit positions",
  "Access for future servicing once the unit is installed",
];

const timelines = [
  { stage: "Enquiry to on-site assessment", timeframe: "Usually within a few business days, sooner where possible ahead of winter" },
  { stage: "Assessment to written quote", timeframe: "Typically 1–3 business days" },
  { stage: "Quote acceptance to installation date", timeframe: "Varies seasonally — book early for autumn/winter installs" },
  { stage: "Single split system installation", timeframe: "Half a day to a full day" },
  { stage: "Multi-split or small ducted system", timeframe: "One to two days depending on access and zones" },
  { stage: "Larger ducted retrofit", timeframe: "Two to three days depending on roof access and ductwork" },
];

const prepareItems = [
  "Clear access to the areas where indoor and outdoor units will be installed",
  "Let us know about pets so we can plan around them on the day",
  "Have parking available close to the property for our vehicle and equipment",
  "Point out any household items, artwork or fittings near work areas that need extra care",
  "Confirm who will be on-site to receive the handover walkthrough",
];

const faqItems = [
  {
    question: "How long does the whole process take from first call to a working system?",
    answer:
      "For a straightforward single split system, it's realistic to go from enquiry to a commissioned system within one to three weeks, depending on how quickly the quote is accepted and current seasonal demand. Ducted systems and busier winter periods can extend this timeline — we'll always give you a realistic estimate rather than the fastest possible one.",
  },
  {
    question: "Do I need to be home for the on-site assessment and for installation day?",
    answer:
      "Yes, we need someone present who can give us access to the property and answer questions about how each room is used. For installation day, it's helpful if someone is available at the start and for the final walkthrough, even if you don't need to be present for the entire job.",
  },
  {
    question: "Is the quote genuinely fixed, or can the price change on the day?",
    answer:
      "Our written quotes are fixed based on the scope assessed on-site. If something unexpected is discovered once work begins — such as damaged existing wiring or unsuitable old pipework — we'll explain it and agree any change with you before proceeding, not after the invoice arrives.",
  },
  {
    question: "What happens if the weather is bad on my scheduled installation day?",
    answer:
      "Most installation work can proceed in typical Highlands weather, including light rain and cold mornings. In the case of genuinely unsafe conditions for outdoor unit mounting or roof access, we'll contact you in advance to reschedule rather than risk a rushed or unsafe job.",
  },
  {
    question: "Will you remove and dispose of my old air conditioning unit?",
    answer:
      "Yes, we can decommission and remove an existing system as part of the job, including safe recovery of any remaining refrigerant by our licensed technicians, which is a legal requirement rather than something that can be skipped to save time.",
  },
  {
    question: "Do you handle the electrical work, or do I need to organise a separate electrician?",
    answer:
      "We handle the electrical work associated with your installation as part of the one job, so there's no need to separately coordinate an electrician. All wiring is completed to Australian standards and tested as part of commissioning.",
  },
  {
    question: "What documentation will I receive at the end?",
    answer:
      "You'll receive your manufacturer warranty documentation, electrical compliance certification, our workmanship warranty terms in writing, and an invoice with service record — everything you'd need if you ever had to make a warranty claim or sell the property.",
  },
  {
    question: "How soon should I book before winter to avoid a wait?",
    answer:
      "Demand for heating installations rises through autumn as the weather turns, so booking your assessment in late summer or early autumn generally gives you the most flexibility on installation dates ahead of the coldest months.",
  },
];

const highlights = [
  "Free written quote after an on-site assessment",
  "Heat-load sizing, not floor-area guesswork",
  "Servicing all of the Southern Highlands",
  "ARC-licensed technicians on every job (AU38134)",
  "Genuine parts & manufacturer warranties",
  "Documentation and compliance certificates on handover",
];

const OurProcess = () => {
  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="Our AC Installation Process | Step by Step | ACG Southern Highlands"
        description="How ACG Southern Highlands delivers heating and cooling installs from enquiry to commissioning — heat-load sizing, scheduling, electrical work, handover and aftercare."
      />
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Our Process" }]} />

      {/* Hero */}
      <section className="bg-accent text-accent-foreground py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-6 h-6 border-t-4 border-l-4 border-primary mt-1 flex-shrink-0" />
            <div>
              <p className="text-secondary font-semibold uppercase tracking-widest text-sm mb-2">Quality & Transparency</p>
              <h1 className="text-4xl md:text-5xl font-black leading-tight">Our Process</h1>
            </div>
          </div>
          <p className="text-muted-foreground max-w-2xl">
            Eight stages, from your first enquiry through to ongoing aftercare, built around what actually matters
            for a cool-climate installation: correct sizing, proper commissioning, and honest documentation.
          </p>
          <div className="mt-8 space-y-3">
            {highlights.map((h) => (
              <div key={h} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-acg-green flex-shrink-0" />
                <span className="text-primary-foreground/80 text-sm">{h}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <p className="text-primary font-semibold uppercase tracking-widest text-sm mb-2">Our Process</p>
            <h2 className="text-3xl md:text-4xl font-black text-foreground">The ACG Process, Stage by Stage</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              From first contact to final handover and beyond, every step is designed to deliver a correctly sized
              system with complete transparency along the way.
            </p>
          </div>

          <div className="space-y-0">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className={`flex flex-col md:flex-row items-stretch gap-0 ${
                  index % 2 === 1 ? "md:flex-row-reverse" : ""
                }`}
              >
                <div className="md:w-1/2 bg-accent text-accent-foreground p-10 md:p-14 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-6xl md:text-7xl font-black text-primary/30">{step.number}</span>
                    <step.icon className="w-10 h-10 text-secondary" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-primary-foreground">{step.title}</h3>
                </div>

                <div className="md:w-1/2 bg-muted p-10 md:p-14 flex items-center">
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Site assessment checklist */}
      <section className="py-16 px-4 bg-muted">
        <div className="container mx-auto max-w-4xl space-y-6">
          <h2 className="text-3xl font-black text-foreground">What We Check During a Site Assessment</h2>
          <p className="text-muted-foreground leading-relaxed">
            A proper on-site assessment is what separates a system that copes with a Highlands winter from one that
            doesn't. Here's what our technicians look at before we put together your written quote:
          </p>
          <ul className="grid sm:grid-cols-2 gap-3">
            {checklistItems.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Timelines */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl space-y-6">
          <h2 className="text-3xl font-black text-foreground">Typical Timelines</h2>
          <p className="text-muted-foreground leading-relaxed">
            Every job differs slightly, but these are realistic ranges for each stage of the process. Timelines
            extend somewhat during peak autumn and winter demand, so earlier bookings generally mean more choice of
            installation dates.
          </p>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-4 font-bold text-foreground">Stage</th>
                  <th className="text-left p-4 font-bold text-foreground">Typical Timeframe</th>
                </tr>
              </thead>
              <tbody>
                {timelines.map((t, i) => (
                  <tr key={t.stage} className={i % 2 === 1 ? "bg-muted/50" : ""}>
                    <td className="p-4 text-muted-foreground">{t.stage}</td>
                    <td className="p-4 text-muted-foreground">{t.timeframe}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* What to prepare */}
      <section className="py-16 px-4 bg-muted">
        <div className="container mx-auto max-w-4xl space-y-6">
          <h2 className="text-3xl font-black text-foreground">What to Prepare Before We Arrive</h2>
          <p className="text-muted-foreground leading-relaxed">
            A little preparation on your end helps both the assessment and the installation run smoothly:
          </p>
          <ul className="space-y-3">
            {prepareItems.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl space-y-8">
          <h2 className="text-3xl font-black text-foreground">Process Questions, Answered</h2>
          <div className="space-y-6">
            {faqItems.map((item) => (
              <div key={item.question} className="border border-border rounded-xl bg-card shadow-sm px-6 py-5">
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">{item.question}</h3>
                <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why ACG Process Matters */}
      <section className="py-16 px-4 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-4xl text-center space-y-6">
          <h2 className="text-3xl font-black">Why Our Process Works</h2>
          <p className="text-primary-foreground/80 max-w-2xl mx-auto leading-relaxed">
            Every project we undertake follows this proven eight-stage framework. It ensures nothing is missed,
            timelines are realistic, and you receive a system that's correctly sized for our climate, professionally
            installed, and backed by genuine manufacturer and workmanship support. That's the ACG difference.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <a
              href="/services"
              className="bg-accent text-accent-foreground px-8 py-3 rounded font-bold text-sm hover:opacity-90 transition-opacity"
            >
              Explore Our Services
            </a>
            <a
              href="/contact-us"
              className="border border-primary-foreground text-primary-foreground px-8 py-3 rounded font-bold text-sm hover:bg-primary-foreground hover:text-primary transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      <CTAFooter />
      <SiteFooter />
    </div>
  );
};

export default OurProcess;
